import type { Lang } from "../index";

export interface TrustCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  intro: string;
  sections: {
    local: {
      heading: string;
      body: string;
      items: string[];
    };
    cloud: {
      heading: string;
      body: string;
      chain: {
        label: string;
        primary: string;
        fallback1: string;
        fallback2: string;
      }[];
    };
    subprocessors: {
      heading: string;
      body: string;
      columns: { name: string; purpose: string; retention: string };
      rows: { name: string; purpose: string; retention: string }[];
    };
    notDoing: {
      heading: string;
      items: string[];
    };
    links: {
      heading: string;
      items: { label: string; href: string }[];
    };
    contact: {
      heading: string;
      body: string;
      cta: string;
    };
  };
}

const en: TrustCopy = {
  seo: {
    title: "Trust — where your data goes (Minuta)",
    description:
      "Honest, written data-flow for Minuta: what stays on your computer, which cloud providers we use (Deepgram, OpenAI, Anthropic, Groq, Modal, DeepL, AWS, Stripe), and what we never do with your meeting content.",
  },
  eyebrow: "Trust & transparency",
  h1: "Where your data goes",
  intro:
    "Minuta uses a cloud AI chain for transcription, summaries, and copilot answers. This page documents exactly which data leaves your computer, which providers handle it, and what we never do.",
  sections: {
    local: {
      heading: "What stays on your machine",
      body: "The following never leave your computer unless you explicitly export or share them:",
      items: [
        "Audio capture — Minuta records system audio locally on the desktop app.",
        "Transcript database — every transcript is stored in a local SQLite database on your computer.",
        "Meeting summaries — generated summaries are saved locally, not server-side.",
        "Copilot answers — suggested answers during the meeting are stored in the local database.",
        "Exports — any file you export (text, PDF, etc.) stays on your machine until you choose to send it.",
        "Attached context documents — files you attach to a meeting for copilot grounding stay local.",
      ],
    },
    cloud: {
      heading: "What we send to the cloud, and why",
      body: "To deliver transcription, summaries, speaker identification, copilot answers, and optional translation, Minuta routes narrow payloads through a reviewed multi-provider chain. Audio is sent only for transcription and diarization; text payloads go to summaries and copilot. We do not log these payloads on Minuta's API.",
      chain: [
        {
          label: "Transcription",
          primary: "Deepgram (primary ASR)",
          fallback1: "OpenAI Whisper (fallback)",
          fallback2: "Groq Whisper (last resort)",
        },
        {
          label: "Summaries & copilot",
          primary: "OpenAI gpt-4.1-mini (primary)",
          fallback1: "Anthropic Claude Haiku 4.5 (fallback)",
          fallback2: "Groq (last resort)",
        },
        {
          label: "Speaker diarization",
          primary: "Modal / pyannote — staged audio uploaded to a private short-lived S3 object",
          fallback1: "",
          fallback2: "",
        },
        {
          label: "Translation (optional)",
          primary: "DeepL — transcript text only, never audio",
          fallback1: "",
          fallback2: "",
        },
      ],
    },
    subprocessors: {
      heading: "Subprocessors",
      body: "All subprocessors are listed here and in our Privacy Policy. We update this table whenever the provider chain changes.",
      columns: { name: "Provider", purpose: "Purpose", retention: "Retention note" },
      rows: [
        {
          name: "Deepgram",
          purpose: "Automatic speech recognition (transcription, primary)",
          retention: "No audio retained per Deepgram's zero-retention API contract",
        },
        {
          name: "OpenAI",
          purpose: "Transcription fallback (Whisper); summaries and copilot answers (primary)",
          retention: "Transient processing; data not used for training by default under API terms",
        },
        {
          name: "Anthropic",
          purpose: "Summaries and copilot answers (fallback)",
          retention: "Transient processing; data not used for training under API terms",
        },
        {
          name: "Groq",
          purpose: "Transcription and LLM inference (last-resort fallback)",
          retention: "Transient processing per Groq API terms",
        },
        {
          name: "Modal / pyannote",
          purpose: "Speaker diarization — identifies who spoke when",
          retention: "Audio staging object deleted after diarization completes",
        },
        {
          name: "AWS (S3, Lambda, API Gateway, Cognito, DynamoDB, CloudFront)",
          purpose: "API hosting, auth, account metadata, short-lived audio staging, CDN",
          retention: "Account metadata retained while account is active; staging objects deleted after use",
        },
        {
          name: "DeepL",
          purpose: "Real-time translation (optional, when enabled)",
          retention: "Text only; not retained per DeepL API terms",
        },
        {
          name: "Stripe",
          purpose: "Payment processing and subscription management",
          retention: "Stripe holds card data; we store only the Stripe customer ID and subscription state",
        },
        {
          name: "Google reCAPTCHA v3",
          purpose: "Anti-bot on site forms",
          retention: "Score returned; not persisted by us",
        },
        {
          name: "Sentry (optional)",
          purpose: "Error diagnostics — never includes meeting content",
          retention: "Stack traces and metadata; gated on VITE_SENTRY_DSN env var",
        },
        {
          name: "Plausible (optional)",
          purpose: "Anonymous pageview analytics — no cookies, no meeting content",
          retention: "Aggregate stats; gated on VITE_PLAUSIBLE_DOMAIN env var",
        },
      ],
    },
    notDoing: {
      heading: "What we do not do",
      items: [
        "We do not train AI models on your meeting audio, transcripts, or copilot answers.",
        "We do not retain audio beyond the immediate API call — audio is sent for transcription or diarization and not stored on our servers.",
        "We do not use a meeting bot — Minuta joins no meeting as a participant; it captures audio locally on your computer.",
        "We do not sell or rent your meeting content to third parties, data brokers, or advertisers.",
        "We do not log raw audio, transcripts, prompts, or generated responses at the Minuta API layer.",
        "We do not collect desktop analytics unless you opt in, and opt-in events never carry meeting content.",
      ],
    },
    links: {
      heading: "Further reading",
      items: [
        { label: "Privacy Policy", href: "/privacy" },
        { label: "Terms of Service", href: "/terms" },
        {
          label: "ADR 0002 — Cloud-only architecture decision",
          href: "https://github.com/mgm-tech/meeting-transcriber/blob/main/docs/adr/0002-cloud-only.md",
        },
        {
          label: "ADR 0005 — Cloud provider privacy review",
          href: "https://github.com/mgm-tech/meeting-transcriber/blob/main/docs/adr/0005-cloud-privacy-review.md",
        },
      ],
    },
    contact: {
      heading: "Have a question?",
      body: "If you have questions about how Minuta handles your data, reach us directly.",
      cta: "Contact us",
    },
  },
};

const pt: TrustCopy = {
  seo: {
    title: "Onde seus dados vão — Transparência de dados (Minuta)",
    description:
      "Fluxo de dados honesto e escrito do Minuta: o que fica no seu computador, quais provedores cloud usamos (Deepgram, OpenAI, Anthropic, Groq, Modal, DeepL, AWS, Stripe) e o que nunca fazemos com o conteúdo das suas reuniões.",
  },
  eyebrow: "Confiança e transparência",
  h1: "Onde seus dados vão",
  intro:
    "O Minuta usa uma cadeia de IA na nuvem para transcrição, resumos e respostas do copiloto. Esta página documenta exatamente quais dados saem do seu computador, quais provedores os processam e o que nunca fazemos.",
  sections: {
    local: {
      heading: "O que fica no seu computador",
      body: "Os itens abaixo nunca saem do seu computador, a não ser que você exporte ou compartilhe explicitamente:",
      items: [
        "Captura de áudio — o Minuta grava o áudio do sistema localmente no app desktop.",
        "Banco de transcrições — toda transcrição é armazenada em um banco SQLite local no seu computador.",
        "Resumos de reunião — os resumos gerados ficam salvos localmente, não em servidor.",
        "Respostas do copiloto — as sugestões de resposta durante a reunião ficam no banco local.",
        "Exportações — qualquer arquivo exportado (texto, PDF etc.) fica na sua máquina até você decidir enviá-lo.",
        "Documentos de contexto anexados — arquivos que você anexa para fundamentar o copiloto ficam locais.",
      ],
    },
    cloud: {
      heading: "O que enviamos para a nuvem e por quê",
      body: "Para entregar transcrição, resumos, identificação de locutor, respostas do copiloto e tradução opcional, o Minuta roteia payloads mínimos por uma cadeia multi-provedor revisada. Áudio é enviado apenas para transcrição e diarização; payloads de texto vão para resumos e copiloto. Não registramos esses payloads na API do Minuta.",
      chain: [
        {
          label: "Transcrição",
          primary: "Deepgram (ASR primário)",
          fallback1: "OpenAI Whisper (fallback)",
          fallback2: "Groq Whisper (último recurso)",
        },
        {
          label: "Resumos e copiloto",
          primary: "OpenAI gpt-4.1-mini (primário)",
          fallback1: "Anthropic Claude Haiku 4.5 (fallback)",
          fallback2: "Groq (último recurso)",
        },
        {
          label: "Diarização de locutor",
          primary: "Modal / pyannote — áudio enviado para um objeto S3 privado de curta duração",
          fallback1: "",
          fallback2: "",
        },
        {
          label: "Tradução (opcional)",
          primary: "DeepL — apenas texto da transcrição, nunca áudio",
          fallback1: "",
          fallback2: "",
        },
      ],
    },
    subprocessors: {
      heading: "Subprocessadores",
      body: "Todos os subprocessadores estão listados aqui e na nossa Política de Privacidade. Atualizamos esta tabela sempre que a cadeia de provedores muda.",
      columns: { name: "Provedor", purpose: "Finalidade", retention: "Retenção" },
      rows: [
        {
          name: "Deepgram",
          purpose: "Reconhecimento de fala (transcrição, primário)",
          retention: "Sem retenção de áudio conforme contrato de API zero-retention da Deepgram",
        },
        {
          name: "OpenAI",
          purpose: "Fallback de transcrição (Whisper); resumos e respostas do copiloto (primário)",
          retention: "Processamento transiente; dados não usados para treinamento por padrão nos termos da API",
        },
        {
          name: "Anthropic",
          purpose: "Resumos e respostas do copiloto (fallback)",
          retention: "Processamento transiente; dados não usados para treinamento nos termos da API",
        },
        {
          name: "Groq",
          purpose: "Transcrição e inferência LLM (fallback de último recurso)",
          retention: "Processamento transiente conforme termos da API Groq",
        },
        {
          name: "Modal / pyannote",
          purpose: "Diarização de locutor — identifica quem falou e quando",
          retention: "Objeto de staging de áudio deletado após a diarização",
        },
        {
          name: "AWS (S3, Lambda, API Gateway, Cognito, DynamoDB, CloudFront)",
          purpose: "Hospedagem de API, auth, metadados de conta, staging de áudio de curta duração, CDN",
          retention: "Metadados de conta retidos enquanto a conta está ativa; objetos de staging deletados após uso",
        },
        {
          name: "DeepL",
          purpose: "Tradução em tempo real (opcional, quando ativado)",
          retention: "Apenas texto; não retido conforme termos da API DeepL",
        },
        {
          name: "Stripe",
          purpose: "Processamento de pagamento e gestão de assinatura",
          retention: "A Stripe guarda os dados do cartão; armazenamos apenas o customer ID e o estado da assinatura",
        },
        {
          name: "Google reCAPTCHA v3",
          purpose: "Proteção anti-bot nos formulários do site",
          retention: "Score retornado; não persistido por nós",
        },
        {
          name: "Sentry (opcional)",
          purpose: "Diagnóstico de erros — nunca inclui conteúdo de reunião",
          retention: "Stack traces e metadados; ativado pela env VITE_SENTRY_DSN",
        },
        {
          name: "Plausible (opcional)",
          purpose: "Analytics anônimo de pageview — sem cookies, sem conteúdo de reunião",
          retention: "Estatísticas agregadas; ativado pela env VITE_PLAUSIBLE_DOMAIN",
        },
      ],
    },
    notDoing: {
      heading: "O que não fazemos",
      items: [
        "Não treinamos modelos de IA com o áudio, transcrições ou respostas do copiloto das suas reuniões.",
        "Não retemos áudio além da chamada de API imediata — o áudio é enviado para transcrição ou diarização e não fica armazenado nos nossos servidores.",
        "Não usamos bot de reunião — o Minuta não entra em nenhuma reunião como participante; ele captura áudio localmente no seu computador.",
        "Não vendemos nem alugamos o conteúdo das suas reuniões para terceiros, data brokers ou anunciantes.",
        "Não registramos áudio bruto, transcrições, prompts ou respostas geradas na camada da API do Minuta.",
        "Não coletamos analytics do app desktop a não ser que você opte por isso, e eventos de opt-in nunca carregam conteúdo de reunião.",
      ],
    },
    links: {
      heading: "Leitura adicional",
      items: [
        { label: "Política de Privacidade", href: "/privacy" },
        { label: "Termos de Uso", href: "/terms" },
        {
          label: "ADR 0002 — Decisão de arquitetura cloud-only",
          href: "https://github.com/mgm-tech/meeting-transcriber/blob/main/docs/adr/0002-cloud-only.md",
        },
        {
          label: "ADR 0005 — Revisão de privacidade para provedores cloud",
          href: "https://github.com/mgm-tech/meeting-transcriber/blob/main/docs/adr/0005-cloud-privacy-review.md",
        },
      ],
    },
    contact: {
      heading: "Tem alguma dúvida?",
      body: "Se você tem perguntas sobre como o Minuta trata seus dados, fale diretamente com a gente.",
      cta: "Entre em contato",
    },
  },
};

const es: TrustCopy = {
  seo: {
    title: "Dónde van tus datos — Transparencia de datos (Minuta)",
    description:
      "Flujo de datos honesto y escrito de Minuta: qué se queda en tu computadora, qué proveedores cloud usamos (Deepgram, OpenAI, Anthropic, Groq, Modal, DeepL, AWS, Stripe) y qué nunca hacemos con el contenido de tus reuniones.",
  },
  eyebrow: "Confianza y transparencia",
  h1: "Dónde van tus datos",
  intro:
    "Minuta usa una cadena de IA en la nube para transcripción, resúmenes y respuestas del copiloto. Esta página documenta exactamente qué datos salen de tu computadora, qué proveedores los procesan y qué nunca hacemos.",
  sections: {
    local: {
      heading: "Qué se queda en tu computadora",
      body: "Los siguientes elementos nunca salen de tu computadora a menos que los exportes o compartas explícitamente:",
      items: [
        "Captura de audio — Minuta graba el audio del sistema localmente en la app de escritorio.",
        "Base de transcripciones — cada transcripción se almacena en una base de datos SQLite local en tu computadora.",
        "Resúmenes de reunión — los resúmenes generados se guardan localmente, no en el servidor.",
        "Respuestas del copiloto — las sugerencias de respuesta durante la reunión se almacenan en la base de datos local.",
        "Exportaciones — cualquier archivo exportado (texto, PDF, etc.) queda en tu máquina hasta que decidas enviarlo.",
        "Documentos de contexto adjuntos — los archivos que adjuntas para fundamentar el copiloto quedan locales.",
      ],
    },
    cloud: {
      heading: "Qué enviamos a la nube y por qué",
      body: "Para entregar transcripción, resúmenes, identificación de hablante, respuestas del copiloto y traducción opcional, Minuta enruta payloads mínimos a través de una cadena multi-proveedor revisada. El audio solo se envía para transcripción y diarización; los payloads de texto van a resúmenes y copiloto. No registramos estos payloads en la API de Minuta.",
      chain: [
        {
          label: "Transcripción",
          primary: "Deepgram (ASR primario)",
          fallback1: "OpenAI Whisper (fallback)",
          fallback2: "Groq Whisper (último recurso)",
        },
        {
          label: "Resúmenes y copiloto",
          primary: "OpenAI gpt-4.1-mini (primario)",
          fallback1: "Anthropic Claude Haiku 4.5 (fallback)",
          fallback2: "Groq (último recurso)",
        },
        {
          label: "Diarización de hablante",
          primary: "Modal / pyannote — audio enviado a un objeto S3 privado de corta duración",
          fallback1: "",
          fallback2: "",
        },
        {
          label: "Traducción (opcional)",
          primary: "DeepL — solo texto de transcripción, nunca audio",
          fallback1: "",
          fallback2: "",
        },
      ],
    },
    subprocessors: {
      heading: "Subprocesadores",
      body: "Todos los subprocesadores están listados aquí y en nuestra Política de Privacidad. Actualizamos esta tabla cuando cambia la cadena de proveedores.",
      columns: { name: "Proveedor", purpose: "Finalidad", retention: "Retención" },
      rows: [
        {
          name: "Deepgram",
          purpose: "Reconocimiento de voz (transcripción, primario)",
          retention: "Sin retención de audio según contrato de API zero-retention de Deepgram",
        },
        {
          name: "OpenAI",
          purpose: "Fallback de transcripción (Whisper); resúmenes y respuestas del copiloto (primario)",
          retention: "Procesamiento transitorio; datos no usados para entrenamiento por defecto en los términos de API",
        },
        {
          name: "Anthropic",
          purpose: "Resúmenes y respuestas del copiloto (fallback)",
          retention: "Procesamiento transitorio; datos no usados para entrenamiento en los términos de API",
        },
        {
          name: "Groq",
          purpose: "Transcripción e inferencia LLM (fallback de último recurso)",
          retention: "Procesamiento transitorio según términos de API de Groq",
        },
        {
          name: "Modal / pyannote",
          purpose: "Diarización de hablante — identifica quién habló y cuándo",
          retention: "Objeto de staging de audio eliminado después de la diarización",
        },
        {
          name: "AWS (S3, Lambda, API Gateway, Cognito, DynamoDB, CloudFront)",
          purpose: "Hosting de API, autenticación, metadatos de cuenta, staging de audio de corta duración, CDN",
          retention: "Metadatos de cuenta retenidos mientras la cuenta está activa; objetos de staging eliminados tras el uso",
        },
        {
          name: "DeepL",
          purpose: "Traducción en tiempo real (opcional, cuando está activado)",
          retention: "Solo texto; no retenido según los términos de API de DeepL",
        },
        {
          name: "Stripe",
          purpose: "Procesamiento de pagos y gestión de suscripción",
          retention: "Stripe guarda los datos de tarjeta; solo almacenamos el customer ID y el estado de la suscripción",
        },
        {
          name: "Google reCAPTCHA v3",
          purpose: "Anti-bot en formularios del sitio",
          retention: "Score devuelto; no persistido por nosotros",
        },
        {
          name: "Sentry (opcional)",
          purpose: "Diagnóstico de errores — nunca incluye contenido de reunión",
          retention: "Stack traces y metadatos; activado por la env VITE_SENTRY_DSN",
        },
        {
          name: "Plausible (opcional)",
          purpose: "Analytics anónimo de pageview — sin cookies, sin contenido de reunión",
          retention: "Estadísticas agregadas; activado por la env VITE_PLAUSIBLE_DOMAIN",
        },
      ],
    },
    notDoing: {
      heading: "Lo que no hacemos",
      items: [
        "No entrenamos modelos de IA con el audio, las transcripciones ni las respuestas del copiloto de tus reuniones.",
        "No retenemos audio más allá de la llamada de API inmediata — el audio se envía para transcripción o diarización y no se almacena en nuestros servidores.",
        "No usamos un bot de reunión — Minuta no se une a ninguna reunión como participante; captura el audio localmente en tu computadora.",
        "No vendemos ni alquilamos el contenido de tus reuniones a terceros, data brokers o anunciantes.",
        "No registramos audio crudo, transcripciones, prompts ni respuestas generadas en la capa de la API de Minuta.",
        "No recopilamos analytics de la app de escritorio a menos que optes por ello, y los eventos de opt-in nunca incluyen contenido de reunión.",
      ],
    },
    links: {
      heading: "Lectura adicional",
      items: [
        { label: "Política de Privacidad", href: "/privacy" },
        { label: "Términos de Servicio", href: "/terms" },
        {
          label: "ADR 0002 — Decisión de arquitectura cloud-only",
          href: "https://github.com/mgm-tech/meeting-transcriber/blob/main/docs/adr/0002-cloud-only.md",
        },
        {
          label: "ADR 0005 — Revisión de privacidad para proveedores cloud",
          href: "https://github.com/mgm-tech/meeting-transcriber/blob/main/docs/adr/0005-cloud-privacy-review.md",
        },
      ],
    },
    contact: {
      heading: "¿Tenés una pregunta?",
      body: "Si tenés preguntas sobre cómo Minuta maneja tus datos, escribinos directamente.",
      cta: "Contactanos",
    },
  },
};

export const trustCopy: Record<Lang, TrustCopy> = { en, pt, es };
